import { createRouter, createWebHashHistory } from 'vue-router';
import App from './App.vue';
import { mapActions } from 'pinia'
import { useMainStore } from './stores/mainStore'
import { t } from '@/i18n'   // IE 2026 09 23 / ODOO-341407: i18n
import { openRecord_fromQuery } from '@/utils/openRecordLink'   // IE 2026 09 29 / ODOO-342342 (приёмка ie-growth7 Д8): ссылка open_record из query
import { guideLink_fromQuery, guideLink_keep } from '@/utils/guideLink'   // IE 2026 09 30 / ODOO-343435: вход в guided-тур ссылкой; IE 2026 10 09 / ODOO-343435: + guideLink_keep (ссылка переживает вход гостя)

const routes = [
    {
        path: '/',
        name: 'app',
        component: App,
        children: [
            {
                path: '/:dbid',
                name: 'db',
                component: () => import('./pages/Page.vue'),
                props: route => ({
                    dbid: route.params.dbid,
                    process_model: route.query.process_model,
                    workspace: route.query.workspace,
                    page: route.query.page,
                    action: route.query.action,
                    record: route.query.record,
                    segment: route.query.segment,
                    collection: route.query.collection,
                }),
            },
        ]
    },
    {
        path: '/login',
        name: 'login',
        component: () => import('./pages/Login.vue')
    },
    {
        path: '/select-database',
        name: 'selectDatabase',
        component: () => import('./pages/DatabaseSelector.vue')
    },
    {
        // ODOO-3705: посадка, когда рабочая область по умолчанию не задана или — IE 2026 11 02 / ODOO-349799 (workspace-wording)
        // запрошенного не существует. Раньше в этом случае пользователя
        // выбрасывало на логин, что выглядело как разлогинивание.
        path: '/:dbid/modules',
        name: 'moduleSelector',
        component: () => import('./pages/ModuleSelector.vue')
    },
    {
        path: '/:dbid/login',
        name: 'dbLogin',
        component: () => import('./pages/Login.vue'),
        props: route => ({
            dbid: route.params.dbid,
            process_model: route.query.process_model,
            workspace: route.query.workspace,
            page: route.query.page,
            action: route.query.action,
            record: route.query.record,
            segment: route.query.segment,
            collection: route.query.collection,
        }),
    },
]

import { IS_NEW_INFRA } from './stores/mainStore'

const router = createRouter({
    history: createWebHashHistory(),
    routes,
})

// Chunk-load recovery (ODOO-3581): после пересборки фронта старые lazy-чанки
// удаляются с сервера, поэтому давно открытый SPA ловит 404 на следующем
// динамическом импорте → белый экран. Ловим ошибку загрузки чанка и
// перезагружаем страницу ОДИН раз (свежий index → новые хеши). Флаг в
// sessionStorage не даёт зациклиться, если ошибка настоящая (сервер лежит).
function isChunkLoadError(err) {
    const msg = (err && (err.message || String(err))) || ''
    return /Loading chunk .* failed|Loading CSS chunk .* failed|ChunkLoadError|Failed to fetch dynamically imported module|error loading dynamically imported module|Importing a module script failed/i.test(msg)
}
function recoverFromChunkError(err) {
    if (!isChunkLoadError(err)) return false
    try {
        if (sessionStorage.getItem('chunk_reloaded')) return false // уже пробовали
        sessionStorage.setItem('chunk_reloaded', '1')
    } catch (e) { /* sessionStorage недоступен — просто перезагрузим */ }
    window.location.reload()
    return true
}
router.onError((err) => { recoverFromChunkError(err) })
// Lazy-импорты вне роутинга (компоненты) реджектятся глобально.
window.addEventListener('unhandledrejection', (e) => {
    if (recoverFromChunkError(e.reason)) e.preventDefault()
})
// Успешная навигация = свежая загрузка восстановилась → снять one-shot флаг,
// чтобы следующий деплой снова мог перезагрузить.
router.afterEach(() => { try { sessionStorage.removeItem('chunk_reloaded') } catch (e) { /* ignore */ } })

router.beforeEach((to, from, next) => {
    const store = useMainStore(),
    isLogin = (to.name === 'login' || to.name === 'dbLogin'),
    isSelectDatabase = (to.name === 'selectDatabase')
    store.router = router

    // Allow authenticated users to access database selector.
    // If not authenticated yet but coredatabases cookie exists — restore session first.
    if (isSelectDatabase) {
        // DR-016 compatibility mode: /select-database does not exist on old infra
        if (!IS_NEW_INFRA) {
            next({ name: 'login', replace: true })
            return
        }
        if (store.isAuthenticated) {
            next()
        } else if (store.getCookie('session_key_database_coredatabases')) {
            // Synthesize 'to' for coredatabases so check_sessionKey restores it
            var coreTo = Object.assign({}, to, {
                name: 'db',
                params: Object.assign({}, to.params, { dbid: 'database_coredatabases' }),
            })
            store.check_sessionKey(coreTo, function(target) {
                // After session restore, force navigation to /select-database
                next({ name: 'selectDatabase', query: to.query, replace: true })
            })
        } else {
            next({ name: 'login' })
        }
        return
    }

    if (!store.hide_console) console.log('router.beforeEach', from.name, ' >>> ', to.name, to.params, to.query)

    if (to.query.where) {
        // фильтры стола из ссылки (INFO-лист XLSX): применяются при открытии стола
        store.where_fromURL = to.query.where
        to.query.where = null
    }
    if (to.query.open_record) {
        // IE 2026 08 20 / ODOO-336291 (mobile UX): deep link на запись (письма S&OP) —
        // форма откроется после загрузки страницы стола (workspace_show_widgets)
        // IE 2026 09 29 / ODOO-342342 (приёмка ie-growth7 Д8): + цель ссылки (стол, страница) — страница уже построена: забирает workspace_activate; ключи ссылки
        // удаляются из query, а не зануляются: open_record=null менял ключ страницы (App.routeViewKey), и уже построенная страница создавалась заново (utils/openRecordLink)
        store.openRecord_fromURL = openRecord_fromQuery(to.query)
    }
    if (to.query.guide_path) {
        // IE 2026 09 30 / ODOO-343435: ?guide_path=<id пути>&step=<n> — тур начнётся после открытия стола (workspace_show_widgets / workspace_activate,
        // utils/guideLink); ключи удаляются из query (как open_record), выключатель fe.guide.tour
        store.guideLink_fromURL = guideLink_fromQuery(to.query)
        // IE 2026 10 09 / ODOO-343435: гость без сессии — ссылка видна здесь, на первом переходе (и на форме входа, если ключи остались в адресе), а стол откроется
        // после входа: login делает store.$reset(), первый вход в браузере ещё и перезагружает страницу на язык базы. Запоминаем в sessionStorage вкладки
        // (база адреса — to.params.dbid), стол заберёт guideLink_take. Тур на форме входа не стартует
        guideLink_keep(store.guideLink_fromURL, to.params.dbid)
    }
    if (to.query.action) {
        store.action_id_onOpening = to.query.action
        // store.widgets_onOpening = [{ action_name:'action-front-2025-01-31', command:'widget_open', widget:'widget-record', item:{
        //     id: to.query.action || to.query.record,
        //     segment: to.query.segment,
        //     _meta: { collection: to.query.collection || 'workflow.activities' },
        // }}]
        to.query.action = null
        to.query.record = null
        to.query.segment = null
        to.query.collection = null
    }

    // «Copy widgets as link» (ит.3 п.4.3): набор виджетов с layout из URL,
    // применяется в workspace_show_widgets вместо открываемых по умолчанию
    if (to.query.widgets) {
        store.widgets_fromURL = store.decode_urlWidgets(to.query.widgets)
        to.query.widgets = null
    }

    if (!isLogin && store.isAuthenticated) {
        if (store.user?.database?.dbid === to.params.dbid) {
            store._dbid_rebind = null // ODOO-3241: rebind удался — сбросить guard петли
        }
        if (store.user?.database?.dbid !== to.params.dbid) {
            if (store.user?.database?.dbid && !to.params.dbid) { // 2025-11-19
                next({ name:'db', params: { ...to.params,
                        dbid: store.user.database.dbid,
                    }, query: to.query
                })
            } else {
                // ODOO-3241: dbid рассинхрон store↔route — НЕ auth-fail. Если у route-базы
                // есть session-кука — восстановить сессию штатным путём (check_sessionKey
                // сам сделает next(to) или logout при невалидной сессии). _dbid_rebind —
                // защита от петли, если бэкенд не подменил database в ответе.
                let rebind_session_key = null
                try { rebind_session_key = store.getCookie(`session_key_${to.params.dbid}`) } catch (e) { /* ignore */ }
                if (rebind_session_key && store._dbid_rebind !== to.params.dbid) {
                    store._dbid_rebind = to.params.dbid
                    // ODOO-3705: смена базы по ссылке/адресной строке идёт МИМО set_database,
                    // поэтому контекст прежней базы оставался живым и её стол подставлялся в
                    // адрес целевой (наблюдалось: workspace-databases ядра в URL Биосферы).
                    // Сбрасываем ровно то же, что и штатное переключение.
                    store.activeWORKSPACE = null
                    store.WORKSPACES = {}
                    store.menuTree = []
                    store.process_model_record = { id: '' }
                    store.process_model_current = '' // ODOO-3749: current — атрибут базы
                    store.recentWorkspaces = []
                    store.user_defaults = null
                    store.database_attrs = []
                    store.check_sessionKey(to, next)
                } else {
                    store.logout(to, next, false, '', `router: dbid mismatch store=${store.user?.database?.dbid} route=${to.params.dbid}`)
                }
            }
            return
        } else if (to.name === 'db' && !to.query.process_model && store.process_model_record?.id
                   && (!store.process_model_current || store.process_model_record.id !== store.process_model_current)) {
            // ODOO-3749: адрес без модели = ТЕКУЩАЯ модель базы, дефолт в URL не пишем —
            // такие ссылки из истории браузера всегда открывают актуальную модель.
            // Дописываем модель в чистый адрес только когда работаем в ИСТОРИЧЕСКОЙ
            // (внутренняя навигация не должна молча вернуть пользователя в текущую).
            next({ name:to.name, params:to.params, query: { ...to.query,
                process_model: store.process_model_record.id,
            }})
            return
        } else if (to.name === 'db' && !to.query.workspace && store.activeWORKSPACE?.workspace) {
            next({ name:to.name, params:to.params, query: { ...to.query,
                workspace: store.activeWORKSPACE.workspace,
            }})
            return
        } else if (!store.process_model_record?.id
                   || (to.query.process_model && to.query.process_model !== store.process_model_record.id)) {
            // ODOO-3749: чистый адрес при загруженной модели — НЕ повод для setup:
            // по конвенции это и есть текущая модель. Setup нужен, когда модели ещё
            // нет или явно запрошена другая.
            store.setup(to, next)
            return
        } else if (!store.menuTree.length) {
            store.get_menuTree(to, next)
            return
        } else if (!to.query.workspace) {
            // ODOO-3241: отсутствие workspace в query — НЕ auth-fail: не выкидывать
            // на логин, а вести на дефолтный workspace пользователя (как ветка выше
            // с activeWORKSPACE). Logout остаётся только если дефолта нет.
            // ЛИЧНЫЙ дефолт (Set workspace as my default) главнее сессионного
            // (user.workspace_onOpening = дефолт базы): если личные настройки ещё
            // не загружены — сначала подгружаем, иначе вход всегда уводил на
            // дефолт базы (DataHub), игнорируя выбор пользователя
            if (store.user_defaults === null && to.name === 'db' && typeof store.get_user_defaults === 'function') {
                store.get_user_defaults(() => { router.push({ name:to.name, params:to.params, query:to.query }) })
                next(false)
                return
            }
            const workspace_onOpening = store.user_defaults?.workspace_onOpening || store.user?.workspace_onOpening
            if (to.name === 'db' && workspace_onOpening) {
                next({ name:to.name, params:to.params, query: { ...to.query,
                    workspace: workspace_onOpening,
                }})
                return
            }
            // ODOO-3705: НЕ разлогинивать. Пользователь аутентифицирован, просто
            // некуда его вести: у него нет личного дефолта, у базы не задан свой,
            // и запрошенного стола/страницы не существует. Ведём на выбор модуля
            // и объясняем причину.
            store.moduleSelectorReason = t('The default workspace is not set or the requested one was not found in this database — choose a module.')   // IE 2026 09 23 / ODOO-341407: i18n
            if (to.name === 'db') {
                next({ name: 'moduleSelector', params: { dbid: to.params.dbid } })
                return
            }
            return store.logout(to, next, false, '', 'router: authenticated but no workspace in query')
        }

        // from // TODO check change settings
        // IE 2026 09 29 / ODOO-342781: роль «только чтение» — неявное сохранение при уходе со страниц настроек / задач пропускаем (без тоста: пользователь ничего не сохранял)
        if (((from?.name || '').includes('page-settings') || (from?.name || '').includes('page-tasks')) && store.activeWIDGET && !store.readonly_on()) {
            const action_save = {command:'onSaveRecord__Click', record_sendToBackend_exclude:['_doc']}
            store.saveRecord(store.activeWIDGET.vueObj, store.activeWIDGET.record, action_save, {
                parentAction: 'router.beforeEach',
            })
        }
    }

    window.scrollTo(0, 0);
    if (!isLogin && !store.isAuthenticated) {
        let session_key = null
        try { session_key = store.getCookie(`session_key_${to.params.dbid}`) } catch (e) { /* ignore */ }
        if (session_key) {
            if (store._checkingSession) return
            store._checkingSession = true
            // Safety timer: if check_sessionKey hangs (network/JS error), force redirect after 10s
            var redirectFired = false
            var safetyTimer = setTimeout(function() {
                if (redirectFired) return
                redirectFired = true
                document.cookie = `session_key_${to.params.dbid}=; Max-Age=0; path=/`
                document.cookie = `session_key=; Max-Age=0; path=/`
                store.logout(to, next, false, '', 'router: check_sessionKey safety timeout')
            }, 10000)
            var wrappedNext = function(arg) {
                if (redirectFired) return
                redirectFired = true
                clearTimeout(safetyTimer)
                next(arg)
            }
            try { store.check_sessionKey(to, wrappedNext) }
            catch (e) {
                clearTimeout(safetyTimer); redirectFired = true
                document.cookie = `session_key_${to.params.dbid}=; Max-Age=0; path=/`
                store.logout(to, next, false, '', 'router: check_sessionKey threw')
            }
            return
        } else {
            return store.logout(to, next, false, '', 'router: not authenticated, no session_key cookie')
        }
    } else if (isLogin && store.isAuthenticated && !to.params.dbid && IS_NEW_INFRA) {
        // Authenticated user opens /#/login → go to database selector
        // (don't auto-pick "current" — user may want to switch tenant/core).
        next({ name: 'selectDatabase', query: to.query, replace: true })
        return
    } else if (isLogin && !store.isAuthenticated && !to.params.dbid && IS_NEW_INFRA) {
        // Auto-login on /#/login (no dbid): на /select-database отправляем ТОЛЬКО
        // при куке coredatabases — его guard восстанавливает сессию исключительно
        // по ней и иначе шлёт обратно на login. Прежняя проверка «любая
        // session_key_database_*» с протухшей кукой базы (без coredatabases)
        // зацикливала login ↔ select-database синхронным редиректом — вкладка
        // намертво висла на 100% CPU ещё до отрисовки формы.
        try {
            if (store.getCookie('session_key_database_coredatabases')) {
                next({ name: 'selectDatabase', query: to.query, replace: true })
                return
            }
        } catch (e) { /* fall through */ }
        next()
        return
    } else {
        if (to.name === 'db') {
            const WORKSPACE = store.WORKSPACES[to.query.workspace]
            if (!WORKSPACE) {
                store.workspace_open(to.query.workspace, to.query.page)
                return
            }
            if (!to.query.page) {
                next({ name:to.name, params:to.params, query: { ...to.query,
                    page: WORKSPACE.active_page || Object.keys(WORKSPACE.PAGES)[0],
                }})
                return
            }
            // IE 2026 10 28 / ODOO-5800 (заказ Игоря: страница настроек стола — из меню задач, не из списка страниц): переход на страницу настроек загруженного стола, которой нет в списке страниц, — клон шаблона бэка
            // (WORKSPACE.settings_page) последней страницей, без подмены на первую страницу
            if (to.query.page === 'page-settings' && !(to.query.page in WORKSPACE.PAGES) && store.settingsPage_ensure?.(to.query.workspace)) {
                next()
                return
            }
            if (!(to.query.page in WORKSPACE.PAGES)) {
                let page = WORKSPACE.active_page
                if (!(page in WORKSPACE.PAGES)) {
                    page = Object.keys(WORKSPACE.PAGES)[0]
                }
                next({ name:to.name, params:to.params, query: { ...to.query,
                    page,
                }})
                return
            }
        }

        next()
        return
    }
})

router.afterEach((to, from) => {
    const store = useMainStore()
    if (!store.hide_console) console.log('router.afterEach')

    // ODOO-3705: навигация завершилась — снимаем оверлей переключения базы.
    // Именно здесь, а не в beforeEach: промежуточные шаги (workspace_open,
    // достановка page в query) навигацию прерывают, и afterEach до них не доходит.
    if (store.dbSwitching) store.dbSwitching_stop()

    // Browser tab title: "<database title> · WebSCM" when a database is selected,
    // plain "WebSCM" on initial screens (login / select-database). ODOO-3181
    const dbTitle = store.user?.database?.title
    document.title = (to.name === 'db' && dbTitle) ? `${dbTitle} · WebSCM` : 'WebSCM'

    const lastMatchedRoute = to.matched[to.matched.length - 1]
    if (lastMatchedRoute) {
        // const props = lastMatchedRoute.props.default
        // if (props && props.workspace && props.page) {
        if (to.query?.workspace && to.query?.page) {
            let WORKSPACE = store.WORKSPACES[to.query.workspace],
                PAGE = WORKSPACE?.PAGES[to.query.page]
            if (PAGE?.vueObj) {
                store.workspace_activate(to.query.workspace, to.query.page)
            }
        }
    }
})

export default router;
